import { useState, useMemo } from 'react';
import { Head, Link } from '@inertiajs/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useSidebarState } from '@/components/CollapsibleSidebar';

/**
 * Book data for context.
 */
interface TomeBook {
    id: number;
    title: string;
    slug: string;
    hue: number;
}

/**
 * Chapter data for context.
 */
interface TomeChapter {
    id: number;
    title: string;
}

/**
 * Page data for navigation.
 */
interface TomePage {
    id: number;
    name: string;
    slug: string;
    is_published?: boolean;
    is_current?: boolean;
}

/**
 * Current page with content.
 */
interface CurrentPage {
    id: number;
    name: string;
    slug: string;
    content: string | null;
    is_published: boolean;
}

/**
 * Chapter with pages for sidebar.
 */
interface ChapterWithPages {
    id: number;
    title: string;
    pages: TomePage[];
}

/**
 * Props for the Page view.
 */
interface Props {
    book: TomeBook | null;
    chapter: TomeChapter | null;
    chapters: ChapterWithPages[];
    currentPage: CurrentPage;
    prevPage: TomePage | null;
    nextPage: TomePage | null;
    isGm: boolean;
}

/**
 * Public Page view - displays a single tome page with navigation.
 */
export default function Page({ book, chapter, chapters, currentPage, prevPage, nextPage, isGm }: Props) {
    const { isOpen: sidebarOpen, isMobile, toggle: toggleSidebar, close: closeSidebar } = useSidebarState(true, false);

    // Find the current chapter ID to expand it by default
    const currentChapterId = useMemo(() => {
        // Find the chapter that contains the current page
        const currentChapter = chapters.find(ch =>
            ch.pages.some(p => p.is_current)
        );
        return currentChapter?.id ?? null;
    }, [chapters]);

    // Track which chapters are expanded (by chapter ID)
    // Initialize with the current chapter expanded
    const [expandedChapters, setExpandedChapters] = useState<Set<number>>(() => {
        const initial = new Set<number>();
        if (currentChapterId !== null) {
            initial.add(currentChapterId);
        }
        return initial;
    });

    // Toggle a chapter's expanded state
    const toggleChapter = (chapterId: number) => {
        setExpandedChapters(prev => {
            const next = new Set(prev);
            if (next.has(chapterId)) {
                next.delete(chapterId);
            } else {
                next.add(chapterId);
            }
            return next;
        });
    };

    // Generate colors from book hue
    const hue = book?.hue ?? 30;
    const accentColor = `hsl(${hue}, 50%, 40%)`;

    return (
        <>
            <Head title={`${currentPage.name} - Archivist's Tome`} />

            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex relative">
                {/* Mobile overlay */}
                {isMobile && sidebarOpen && (
                    <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={closeSidebar} />
                )}

                {/* Sidebar - Table of Contents */}
                <aside className={`${sidebarOpen ? 'w-72' : 'w-0'} transition-all duration-300 bg-slate-800/80 border-r border-amber-900/30 overflow-hidden flex-shrink-0 ${isMobile ? 'fixed inset-y-0 left-0 z-50' : 'relative'}`}>
                    <div className="w-72 h-full flex flex-col">
                        {/* Book Header */}
                        <div className="p-4 border-b border-amber-900/30" style={{ backgroundColor: `${accentColor}20` }}>
                            <Link href="/tome" className="text-xs text-amber-200/60 hover:text-amber-200">
                                ← Bookshelf
                            </Link>
                            {book && (
                                <Link href={`/tome/${book.slug}`} className="block mt-2">
                                    <h1 className="text-lg font-serif text-amber-200 hover:text-amber-100">
                                        📖 {book.title}
                                    </h1>
                                </Link>
                            )}
                        </div>

                        {/* Chapters and Pages - Collapsible */}
                        <nav className="flex-1 overflow-y-auto p-3">
                            {chapters.map((ch) => {
                                const isExpanded = expandedChapters.has(ch.id);
                                const hasCurrentPage = ch.pages.some(p => p.is_current);

                                return (
                                    <div key={ch.id} className="mb-2">
                                        {/* Chapter header - clickable to toggle */}
                                        <button
                                            onClick={() => toggleChapter(ch.id)}
                                            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left transition-colors hover:bg-amber-800/20 ${
                                                hasCurrentPage ? 'text-amber-200' : 'text-amber-200/60'
                                            }`}
                                        >
                                            {/* Chevron icon - rotates when expanded */}
                                            <svg
                                                className={`w-3 h-3 flex-shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                            <span className="text-xs font-semibold uppercase tracking-wider">
                                                {ch.title}
                                            </span>
                                            {/* Page count badge */}
                                            <span className="ml-auto text-[10px] text-amber-200/40">
                                                {ch.pages.length}
                                            </span>
                                        </button>

                                        {/* Pages list - collapsible with animation */}
                                        <div
                                            className={`overflow-hidden transition-all duration-200 ease-in-out ${
                                                isExpanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
                                            }`}
                                        >
                                            <ul className="space-y-1 mt-1 ml-3">
                                                {ch.pages.map((page) => (
                                                    <li key={page.id}>
                                                        <Link
                                                            href={`/tome/page/${page.slug}`}
                                                            onClick={() => isMobile && closeSidebar()}
                                                            className={`block px-3 py-2 rounded text-sm transition-colors ${
                                                                page.is_current
                                                                    ? 'bg-amber-800/40 text-amber-100 border-l-2 border-amber-500'
                                                                    : 'text-amber-200/80 hover:bg-amber-800/20 hover:text-amber-100'
                                                            }`}
                                                        >
                                                            {page.name}
                                                            {isGm && !page.is_published && (
                                                                <span className="ml-2 text-xs text-yellow-400">(Draft)</span>
                                                            )}
                                                        </Link>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                );
                            })}
                        </nav>
                    </div>
                </aside>

                {/* Toggle Button */}
                <button
                    onClick={toggleSidebar}
                    className={`fixed top-4 z-50 p-2.5 bg-slate-800/90 border border-amber-900/30 rounded-lg text-amber-200 hover:bg-amber-800/30 transition-all duration-300 ${isMobile ? 'left-4' : ''}`}
                    style={!isMobile ? { left: sidebarOpen ? 'calc(18rem + 0.5rem)' : '0.5rem' } : undefined}
                >
                    <span className="block w-5 h-5 relative">
                        <span className={`absolute left-0 w-5 h-0.5 bg-current transition-all duration-300 ${sidebarOpen ? 'top-2 rotate-45' : 'top-1'}`} />
                        <span className={`absolute left-0 top-2 w-5 h-0.5 bg-current transition-all duration-300 ${sidebarOpen ? 'opacity-0' : 'opacity-100'}`} />
                        <span className={`absolute left-0 w-5 h-0.5 bg-current transition-all duration-300 ${sidebarOpen ? 'top-2 -rotate-45' : 'top-3'}`} />
                    </span>
                </button>

                {/* Main Content */}
                <main className="flex-1 overflow-y-auto">
                    <div className="max-w-4xl mx-auto p-8 pt-16 md:pt-8">
                        <PageContent page={currentPage} chapter={chapter} book={book} prevPage={prevPage} nextPage={nextPage} isGm={isGm} />
                    </div>
                </main>
            </div>
        </>
    );
}

/**
 * PageContent - Displays the page content with markdown rendering and navigation.
 */
function PageContent({ page, chapter, book, prevPage, nextPage, isGm }: {
    page: CurrentPage;
    chapter: TomeChapter | null;
    book: TomeBook | null;
    prevPage: TomePage | null;
    nextPage: TomePage | null;
    isGm: boolean;
}) {
    const hue = book?.hue ?? 30;
    const accentColor = `hsl(${hue}, 50%, 40%)`;

    return (
        <article className="bg-gradient-to-br from-amber-50/5 to-amber-100/5 border border-amber-900/30 rounded-lg shadow-2xl">
            {/* Breadcrumb */}
            <div className="px-6 pt-4 flex items-center gap-2 text-sm text-amber-200/60">
                <Link href="/tome" className="hover:text-amber-200">📚 Bookshelf</Link>
                {book && (
                    <>
                        <span>→</span>
                        <Link href={`/tome/${book.slug}`} className="hover:text-amber-200">{book.title}</Link>
                    </>
                )}
                {chapter && (
                    <>
                        <span>→</span>
                        <span style={{ color: accentColor }}>{chapter.title}</span>
                    </>
                )}
            </div>

            {/* Page Header */}
            <header className="p-6 border-b border-amber-900/20 bg-gradient-to-r from-amber-900/10 to-transparent">
                <h1 className="text-2xl font-serif text-amber-100">{page.name}</h1>
                {isGm && !page.is_published && (
                    <span className="inline-block mt-2 px-2 py-0.5 bg-yellow-600 text-white text-xs rounded">Draft</span>
                )}
            </header>

            {/* Page Content */}
            <div className="p-6 prose prose-invert prose-amber max-w-none">
                {page.content ? (
                    <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                            h1: ({ children }) => <h1 className="text-2xl font-serif text-amber-200 border-b border-amber-900/30 pb-2 mb-4">{children}</h1>,
                            h2: ({ children }) => <h2 className="text-xl font-serif text-amber-200 mt-6 mb-3">{children}</h2>,
                            h3: ({ children }) => <h3 className="text-lg font-serif text-amber-200 mt-4 mb-2">{children}</h3>,
                            p: ({ children }) => <p className="text-amber-100/90 leading-relaxed mb-4">{children}</p>,
                            a: ({ href, children }) => <a href={href} className="text-amber-400 hover:text-amber-300 underline">{children}</a>,
                            ul: ({ children }) => <ul className="list-disc list-inside text-amber-100/90 mb-4 space-y-1">{children}</ul>,
                            ol: ({ children }) => <ol className="list-decimal list-inside text-amber-100/90 mb-4 space-y-1">{children}</ol>,
                            li: ({ children }) => <li className="text-amber-100/90">{children}</li>,
                            blockquote: ({ children }) => <blockquote className="border-l-4 border-amber-600 pl-4 italic text-amber-200/80 my-4">{children}</blockquote>,
                            code: ({ children }) => <code className="bg-slate-800 px-1.5 py-0.5 rounded text-amber-300 text-sm">{children}</code>,
                            pre: ({ children }) => <pre className="bg-slate-800 p-4 rounded overflow-x-auto my-4">{children}</pre>,
                            img: ({ src, alt }) => <img src={src} alt={alt || ''} className="max-w-full h-auto rounded border border-amber-900/30 my-4" />,
                            hr: () => <hr className="border-amber-900/30 my-6" />,
                            table: ({ children }) => <table className="w-full border-collapse my-4">{children}</table>,
                            th: ({ children }) => <th className="border border-amber-900/30 bg-amber-900/20 px-3 py-2 text-left text-amber-200">{children}</th>,
                            td: ({ children }) => <td className="border border-amber-900/30 px-3 py-2 text-amber-100/90">{children}</td>,
                        }}
                    >
                        {page.content}
                    </ReactMarkdown>
                ) : (
                    <p className="text-amber-200/50 italic">This page is empty. The scribe has yet to fill these pages...</p>
                )}
            </div>

            {/* Navigation */}
            <footer className="p-6 border-t border-amber-900/20 flex justify-between">
                {prevPage ? (
                    <Link href={`/tome/page/${prevPage.slug}`} className="text-amber-200/70 hover:text-amber-200 transition-colors">
                        ← {prevPage.name}
                    </Link>
                ) : <div />}
                {nextPage ? (
                    <Link href={`/tome/page/${nextPage.slug}`} className="text-amber-200/70 hover:text-amber-200 transition-colors">
                        {nextPage.name} →
                    </Link>
                ) : <div />}
            </footer>
        </article>
    );
}

